import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import StyledComponent from '~/components/Styled/Styled';
import PlayerHeadshot from '~/components/PlayerHeadshot/PlayerHeadshot';
import get from 'lodash/get';
import { ordinalize } from 'lodash-inflection';
import css from './ScoringCard.scss';

/* eslint-disable react/prefer-stateless-function */
const getTeamLogo = scoring => get(scoring, 'team.logos.small', '');
const getPlayerName = scoring => get(scoring, 'first_initial_and_last_name', '');
const getAssist = (scoring, assistNum) => get(scoring, `assist${assistNum}`, null);
const getAssistName = (scoring, assistNum) => {
  const assist = getAssist(scoring, assistNum);
  return getPlayerName(assist);
};

// Support Extra Base-Hits
const extraBaseHitType = {
  Double: '2B',
  Triple: '3B',
  'Home Run': 'HR',
};

const getScoringStrength = goalStrength => {
  switch (goalStrength) {
    case 'power play':
      return '(PP)';

    case 'short-handed':
      return '(SH)';
    default:
      return null;
  }
};

class ScoringCard extends React.Component {
  render() {
    const { scoring, sportType } = this.props;
    const extraBaseHitGameTotal = get(scoring, 'extra_base_hit_game_total', '');
    const extraBaseHitSeasonTotal = get(scoring, 'extra_base_hit_season_total', '');
    const scoringTypeDescription = get(scoring, 'score_type_description', '');
    const minutes = get(scoring, 'minutes', '0');
    const seconds = get(scoring, 'seconds', '0');
    const scoreTimeExists =
      minutes && seconds && minutes.toString().length > 0 && seconds.toString().length > 0;

    let playerActionStat;

    const FootballScoreTime = () => {
      if (sportType === 'football' && scoreTimeExists) {
        const scoreTimeFormatted = `${minutes}:${seconds.toString().padStart(2, '0')}`;

        return <span className={css.footballScoreTime}>{scoreTimeFormatted} -</span>;
      }

      return <span />;
    };

    if (sportType === 'baseball' && extraBaseHitGameTotal && extraBaseHitSeasonTotal) {
      const extraBaseHits = moment.localeData().ordinal(extraBaseHitSeasonTotal);

      playerActionStat = `${extraBaseHits} ${extraBaseHitType[scoring.extra_base_hit_type]}`;
    }

    if (sportType === 'football' && scoringTypeDescription) {
      playerActionStat = `${scoringTypeDescription}`;
    }

    /* prettier-ignore */
    return (
      <div key="scoring" className={css.scoringCard}>
        <div className={css.playerInfo}>
          <PlayerHeadshot player={scoring.player} size={'small'} />
          <div className={css.playerText}>
            <div className={css.playerTextInnerContainer}>
              <img alt="" className={css.teamLogo} src={getTeamLogo(scoring)} />
              <div className={css.playerName}>
                <div className={css.scorer}>
                  <span className={css.scoringPlayer}>{getPlayerName(scoring.player)}</span>
                  {playerActionStat ? (<div className={css.extraBaseHitStat}>({playerActionStat})</div>) : null}
                </div>
                {scoring.goal_number_season && (
                  <div className={css.seasonGoalNumber}>
                    ({ordinalize(scoring.goal_number_season)})&nbsp;
                  </div>
                )}
                {!!scoring.goal_strength && (
                  <div className={css.seasonGoalNumber}>
                    {getScoringStrength(scoring.goal_strength)} {scoring.empty_net ? `(EN)` : null}
                  </div>
                )}
              </div>
            </div>
            <div>
              {scoring.assist1 && (
                <div className={css.assisters}>
                  {getAssist(scoring, 1) ? `Assisted by ${getAssistName(scoring, 1)}` : ''}
                  {getAssist(scoring, 1) && getAssist(scoring, 2)
                    ? ` and ${getAssistName(scoring, 2)}`
                    : ''}
                </div>
              )}

              {!scoring.description && !scoring.assist1 && (
                <div className={css.assisters}>Unassisted</div>
              )}
              {scoring.description && <div className={css.assisters}>
                <FootballScoreTime />
                {scoring.description}
              </div>}
            </div>
          </div>
        </div>

        {!!scoring.minute && !!scoring.second && (
          <div className={css.time}>
            {scoring.minute}:{scoring.second > 9 ? '' : '0'}
            {scoring.second}
          </div>
        )}

        {!scoring.minute && !!scoring.second && <div className={css.time}>0:{scoring.second}</div>}
      </div>
    );
  }
}

ScoringCard.displayName = 'ScoringCard';

ScoringCard.propTypes = {
  scoring: PropTypes.object.isRequired,
  sportType: PropTypes.string.isRequired,
};

export default StyledComponent(ScoringCard, [css]);
